import React, {useState, useEffect, useMemo, lazy, Suspense} from 'react';
import TopBar from '../../Components/TopBar';
import { useRound, useJobs } from '../../Data/Rounds';
import { useParams } from 'react-router-dom';
import Page from '../../Components/Containers/Page';
import Loading, { PageLoading } from '../../Components/Loading';
import Statuses from '../../Components/Widgets/Jobs/Statuses';
import API from '../../Services/API';
import EditRound from './EditRound';
import Reordering from './Reordering';
import ScheduleRound from './ScheduleRound';
import Grid from '../../Components/Formatting/Grid';
import Duration from '../../Components/Formatting/Duration';
import Money from '../../Components/Formatting/Money';
import { useDialogues } from '../../Components/Dialogues';
import Tabs, { Tab } from '../../Components/Containers/Tabs/Tabs';
import Section, { Sections } from '../../Components/Containers/Section';
import { sleep, useRedirect } from '../../Services/helpers';
import ScheduleJob from '../Customer/ScheduleJob';
import MarkJobAsDone from '../Customer/MarkAsDone';
import MarkRoundDone from './MarkRoundDone';
import Selectable, { useSelectable } from '../../Components/Selectable';
import MassActions from '../Jobs/MassActions';
import JobModal from './JobModal';
import { ulid } from 'ulid';
import { useToasts } from '../../Components/Toasts';
import BarPieTableChart from '../Reports/Components/BarPieTableChart';
import { usePreference } from '../../Data/Preference';
import { getDateLabel } from '../Reports/Components/helpers';

const Job = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Job'));

const Round = () => {
    const {roundId} = useParams();
    const {round: quickRound} = useRound(roundId, true, 'jobs');
    const {round, refresh: refreshRound, reorder, optimise} = useRound(roundId, true, 'jobs');
    const {jobs, refresh: refreshJobs} = useJobs(roundId, true, 'customer');
    const [actioning, setActioning] = useState(null);
    const [isReordering, setIsReordering] = useState(false);
    const {confirm} = useDialogues();
    const [scheduling, setScheduling] = useState(null);
    const [markingJobAsDone, setMarkingJobAsDone] = useState(null);
    const {go, Redirect} = useRedirect();

    const refreshAll = () => Promise.all([refreshRound(), refreshJobs()]);

    const reorderFunc = async (newOrder) => {
        await reorder(newOrder);
        await refreshAll();
    }

    return <Selectable>
        <TheTopBar {...{quickRound, round, isReordering, setActioning, optimise, refreshRound, refreshAll, go, setIsReordering, confirm}} />

        <Page zeroPad>
            {((quickRound || round) && jobs) ?
                <Inner {...{round : quickRound ?? round, jobs, setScheduling, setMarkingJobAsDone, isReordering, setIsReordering, reorder: reorderFunc, refreshAll}} /> :
                <Loading center size="4x" className="mt-10" />}
        </Page>

        <EditRound round={round} isOpen={actioning === 'edit'} onClose={() => setActioning(null)} onSave={refreshRound} />
        <ScheduleRound round={round} jobs={jobs} isOpen={actioning === 'schedule'} onClose={() => setActioning(null)} onSave={refreshJobs} />
        <MarkRoundDone round={round} isOpen={actioning === 'markingDone'} onClose={() => setActioning(null)} onSave={refreshJobs} />

        {/* Individual jobs */}
        <ScheduleJob job={scheduling} onClose={() => setScheduling(null)} then={refreshAll} />
        <MarkJobAsDone job={markingJobAsDone} onClose={() => setMarkingJobAsDone(null)} then={refreshAll} />

        <Redirect />
    </Selectable>
}

const TheTopBar = ({quickRound, round, isReordering, setActioning, optimise, refreshRound, refreshAll, go, setIsReordering, confirm}) => {
    const {toggleIsSelecting} = useSelectable();
    const {working} = useDialogues();
    const {showSuccess} = useToasts();

    return <TopBar left="/plan/rounds"
        title={quickRound?.name ?? round?.name ?? 'Round'}
        dropdownDisabled={isReordering}
        dropdownOptions={[
            {
                title: 'Schedule jobs',
                icon: 'CalendarPlus',
                onClick: () => setActioning('schedule')
            },
            {
                title: 'Mark jobs as done',
                icon: 'Check',
                onClick: () => setActioning('markingDone')
            },
            {
                title: 'Optimise round',
                icon: 'TruckFast',
                onClick: async () => {
                    const closer = working({
                        title: 'Optimising',
                        message: 'This may take a few moments.',
                        secondMessage: 'You can close this dialogue and the optimisation will continue in the background.',
                        dismissible: true,
                    });

                    const optimisationId = ulid();
                    await optimise({optimisationId});
                    let max = 100;

                    const wait = async (optimisationId) => {
                        await sleep(2);
                        const rnd = await refreshRound();
                        if (rnd?.optimisationId === optimisationId || max === 0) {
                            await refreshAll();
                            showSuccess('Round optimisation complete');
                            closer();
                        } else {
                            max--;
                            wait(optimisationId);
                        }
                    }
                    wait(optimisationId);

                },
                feature: 'ROUTE_OPTIMISATION',
            },
            {
                title: 'Reorder jobs',
                icon: 'ArrowsUpDown',
                onClick: () => setIsReordering(true),
            },
            {
                div: true,
                hidden: true,
            },
            {
                title: 'Select jobs',
                icon: 'SquareCheck',
                onClick: () => toggleIsSelecting(),
            },
            {
                div: true
            },
            {
                title: 'Edit round',
                icon: 'Pencil',
                onClick: () => setActioning('edit')
            },
            {
                title: 'Delete round',
                icon: 'Trash',
                onClick: () => confirm(
                    {theme: 'danger', title: 'Delete round', message: 'Really delete this round?'},
                    () => API.delete(`/rounds/${round?.id ?? null}`).then(go('/plan/rounds'))
                )
            },
        ]}
    />
}

const Inner = ({round, jobs, setScheduling, setMarkingJobAsDone, isReordering, setIsReordering, reorder, refreshAll}) => {
    const {replaceSelectable} = useSelectable();
    const [tab, setTab] = usePreference('round.tab');

    useEffect(() => {
        if (jobs) {
            replaceSelectable(jobs.map(job => `${job.customerId}-${job.id}`));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs])

    if (isReordering) {
        return <Suspense fallback={<PageLoading />}>
            <Reordering {...{round, jobs, setIsReordering, reorder}} />
        </Suspense>
    }

    return <>
        <Image round={round} />
        <div className="page-padding">
            <Tabs selected={tab} onChange={setTab}>
                <Tab title="Jobs">
                    <MassActions refresh={refreshAll} />
                    {jobs ? <Jobs {...{jobs, setScheduling, setMarkingJobAsDone}} /> : <Loading size="2x" className="mt-5" center />}
                </Tab>
                <Tab title="Details">
                    {round ? <Panel round={round} roundJobs={jobs} /> : <Loading size="2x" className="mt-5" center />}
                </Tab>
            </Tabs>
        </div>
    </>
}

const Image = ({round}) => {
    if (!round) {
        return null;
    }

    const height = Math.floor(window.innerWidth / 2.5);
    const url = API.url(`/rounds/${round.id}/thumbnail.png?size=${window.innerWidth}x${height}`);

    return <div style={{backgroundImage: `url(${url})`, height}} className="bg-cover bg-center shrink-0 bg-gray-100" />
}

const Panel = ({round}) => {
    return <Sections>
        <Section title="Details">
            <Grid items={[
                [<strong>Name</strong>, round.name],
                [<strong>Value</strong>, <Money pence={round.jobCost} />],
                [<strong>Annual Value</strong>, <Money pence={round.annualValue} />, !!round.annualValue],
                [<strong>Est. Travel time</strong>, <Duration time={round.travelDuration} />],
                [<strong>Keep optimised</strong>, round.keepOptimised ? 'Yes' : 'No'],
            ]} />
        </Section>
        <Section title="Job Statuses">
            {round?.jobStatuses && <Statuses jobs={round.jobStatuses} searchParams={{roundId: round.id}} />}
        </Section>
        {round.jobs && <JobSummary jobs={round.jobs} />}
    </Sections>
}

const  JobSummary = ({jobs}) => {
    const data = useMemo(() => {
        if (!jobs) {
            return null;
        }

        const marshal = (keyer) => {
            const keyed = jobs.reduce((agg, j) => {
                const k = keyer(j);
                agg[k] = j.cost + (agg[k] ?? 0);

                return agg;
            }, {});

            return Object.keys(keyed).reduce((agg, k) => {
                agg.push({
                    label: k,
                    value: keyed[k]
                });
                return agg;
            }, []);
        }

        return {
            week: marshal(j => j.dueWeek ? getDateLabel(j.dueWeek) : '[None]'),
            status: marshal(j => j.status),
        }
    }, [jobs]);

    if (!data) {
        return null;
    }

    return <>
        <BarPieTableChart title="Value by due week"
            groupingLabel="Due week"
            dataLabel="Value"
            labelValueData={data.week}
            dataType="currency"
        />
        <BarPieTableChart title="Value by status"
            groupingLabel="Job status"
            dataLabel="Value"
            labelValueData={data.status}
            dataType="currency"
        />
    </>
}

const Jobs = ({jobs, setScheduling, setMarkingJobAsDone}) => {
    const [openJob, setOpenJob] = useState(null);
    return <>
        <ul className="divide-y divide-gray-200">

            {jobs.sort((a, b) => a.roundPosition - b.roundPosition).map(job => {
                return <Suspense key={job.id} fallback={null}>
                    <Job {...{job, setScheduling, setMarkingJobAsDone, setOpenJob}} />
                </Suspense>
            })}

            {jobs.length === 0 && <li className="py-3 text-muted text-center">
                <p className="font-bold">No jobs</p>
                <p>
                    Add jobs by selecting this round<br />
                    when editing a job on a customer's page.
                </p>
            </li>}
        </ul>
        <JobModal job={openJob} onClose={setOpenJob} />
    </>
}



export default Round;
